import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { useAuth } from 'react-oidc-context'

export default function Login() {
  const [token, setToken] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const navigate = useNavigate()
  const auth = useAuth()

  // Redirect if already authenticated
  useEffect(() => {
    if (auth.isAuthenticated) {
      navigate('/')
    }
  }, [auth.isAuthenticated, navigate])

  // On mount, check local storage for token and pre-fill text area
  useEffect(() => {
    const savedToken = localStorage.getItem('jwt_token')
    if (savedToken) {
      setToken(savedToken)
    }
  }, [])

  const parseJWTPayload = (token: string) => {
    try {
      // JWT format: header.payload.signature
      const parts = token.split('.')
      if (parts.length !== 3) {
        throw new Error('Invalid JWT format')
      }

      // Decode the payload (base64url)
      const payload = parts[1]
      const decodedPayload = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
      return JSON.parse(decodedPayload)
    } catch (err) {
      throw new Error('Failed to parse JWT token')
    }
  }

  const validateToken = (token: string) => {
    if (!token.trim()) {
      throw new Error('Token is required')
    }

    const payload = parseJWTPayload(token)

    // Check if token is expired
    if (payload.exp && payload.exp * 1000 < Date.now()) {
      throw new Error('Token has expired')
    }

    return payload
  }

  const handleOIDCLogin = () => {
    auth.signinRedirect()
  }

  const handleSaveToken = async () => {
    try {
      setIsLoading(true)
      setError('')

      validateToken(token)

      // Store token in local storage for development
      localStorage.setItem('jwt_token', token)

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save token')
    } finally {
      setIsLoading(false)
    }
  }

  const handleClearToken = () => {
    setToken('')
    localStorage.removeItem('jwt_token')
    setError('')
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Login
          </h1>
          <p className="text-gray-600">
            Connect with your authentication provider
          </p>
        </div>

        <div className="space-y-4">
          {/* Primary OIDC Login */}
          <Button
            onClick={handleOIDCLogin}
            disabled={isLoading}
            className="w-full py-3 text-lg"
          >
            {isLoading ? 'Signing In...' : 'Sign In with OIDC'}
          </Button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">Development Only</span>
            </div>
          </div>

          {/* Development JWT Token Input */}
          <div>
            <label htmlFor="jwt-token" className="block text-sm font-medium text-gray-700 mb-2">
              Manual JWT Token (Development)
            </label>
            <textarea
              id="jwt-token"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
              className="w-full h-32 px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none font-mono text-sm"
              disabled={isLoading}
            />
            <p className="mt-1 text-xs text-gray-500">
              Paste your JWT token here for development purposes.
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Development Action Buttons */}
          <div className="flex space-x-3">
            <Button
              onClick={handleSaveToken}
              disabled={!token.trim() || isLoading}
              className="flex-1"
              variant="outline"
            >
              {isLoading ? 'Saving...' : 'Save Token (Dev)'}
            </Button>

            <Button
              onClick={handleClearToken}
              variant="outline"
              disabled={isLoading}
            >
              Clear
            </Button>
          </div>

          {/* Token Info */}
          {token && !error && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
              <p className="text-sm text-blue-600">
                Token loaded {localStorage.getItem('jwt_token') ? 'from local storage' : '(not saved yet)'}
              </p>
            </div>
          )}
        </div>

        <div className="mt-6 pt-6 border-t border-gray-200">
          <p className="text-xs text-gray-500 text-center">
            Production users should use the OIDC sign-in. Manual token input is for development only.
          </p>
        </div>
      </div>
    </div>
  )
} 